import React from 'react';

import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import { translate } from '~l10n/client';

import { PaywallScreens } from '~analytics/types';
import { Badge } from '~shared/components/Badge';
import { Divider } from '~shared/components/Divider';
import {
  IconPlus,
  IconShared,
  IconUser,
} from '~shared/components/icons/standard';
import {
  Menu,
  MenuButton,
  MenuList,
  StyledMenuItem,
} from '~shared/components/Menu';
import { Portal } from '~shared/components/Portal';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { useDrawer } from '~teacher/components/DrawerProvider/DrawerProvider';
import {
  PaywallDispatch,
  usePaywallReducer,
} from '~teacher/components/PaywallDialog';
import { SharedLibraryPaywall } from '~teacher/components/SharedLibraryPaywall';
import {
  CreateWorkspaceDialog,
  useCreateWorkspaceDialog,
} from '~teacher/components/workspaces';
import { JoinWorkspaceWithCodeDialog } from '~teacher/components/workspaces/JoinWorkspaceWithCodeDialog/JoinWorkspaceWithCodeDialog';
import { useJoinWorkspaceWithCodeDialog } from '~teacher/components/workspaces/JoinWorkspaceWithCodeDialog/useJoinWorkspaceWithCodeDialog';
import { Sidebar } from '~teacher/components/WorkspaceSidebar/Sidebar';
import { SidebarItem } from '~teacher/components/WorkspaceSidebar/SidebarItem';
import { useQuizzes } from '~teacher/hook';
import { useWorkspaceList } from '~teacher/hook/workspaces';
import { proCheck } from '~teacher/util/proCheck';

interface SidebarProps {
  currentWorkspaceId?: string;
}
export function WorkspaceSidebar({ currentWorkspaceId }: SidebarProps) {
  const { drawerOpen, setDrawerOpen } = useDrawer();

  const {
    openCreateWorkspaceDialog,
    isCreateWorkspaceDialogOpen,
    closeCreateWorkspaceDialog,
  } = useCreateWorkspaceDialog();

  const {
    openJoinWorkspaceWithCodeDialog,
    isJoinWorkspaceWithCodeDialogOpen,
    closeJoinWorkspaceWithCodeDialog,
  } = useJoinWorkspaceWithCodeDialog();

  const [
    { open: featurePaywallOpen, touch: currentPaywallTouch },
    featurePaywallDispatch,
  ] = usePaywallReducer();

  const { data: quizzes } = useQuizzes({
    select: (quizzes) =>
      [...Object.values(quizzes.itemById)].filter(
        (item) => !item.deleted && item.type !== 'folder'
      ),
  });

  const canCreateLibrary = proCheck.canCreateLibrary();
  const workspaceItemsCount = quizzes?.length ?? 0;
  const applicationContext = useTeacherApplicationContext();

  return (
    <Sidebar
      title={translate('Library')}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
    >
      <>
        <div className="flex-none max-w-88 sm:max-w-92">
          <header className="hidden lg:flex items-center h-14 px-5">
            <h1
              data-testid="quizzes-page-header"
              className="m-0 font-display text-lg font-700 leading-14 text-blue-900 capsize"
            >
              {translate('Library')}
            </h1>
          </header>
          <nav className="pt-6">
            <JoinOrCreateSharedLibraryItem
              openCreateWorkspaceDialog={openCreateWorkspaceDialog}
              openJoinWorkspaceWithCodeDialog={openJoinWorkspaceWithCodeDialog}
              canCreateLibrary={canCreateLibrary}
              featurePaywallDispatch={featurePaywallDispatch}
            />
            <div className="pt-2 pb-6">
              <Divider />
            </div>
            <PersonalItem
              currentWorkspaceId={currentWorkspaceId}
              workspaceItemsCount={workspaceItemsCount}
              applicationContext={applicationContext}
            />
            <WorkspacesList workspaceId={currentWorkspaceId} />
            <div className="pt-4">
              <Divider />
            </div>
          </nav>
        </div>
        <CreateWorkspaceDialog
          isOpen={isCreateWorkspaceDialogOpen}
          onClose={closeCreateWorkspaceDialog}
        />
        <JoinWorkspaceWithCodeDialog
          isOpen={isJoinWorkspaceWithCodeDialogOpen}
          onClose={closeJoinWorkspaceWithCodeDialog}
        />
        <SharedLibraryPaywall
          open={featurePaywallOpen}
          onClose={() => featurePaywallDispatch({ type: 'CLOSE' })}
          paywallScreen={currentPaywallTouch?.[0] ?? null}
        />
      </>
    </Sidebar>
  );
}

function WorkspacesList({ workspaceId }: { workspaceId: string }) {
  const workspaceLists = useWorkspaceList();
  const { setDrawerOpen } = useDrawer();

  if (workspaceLists.isLoading) return null;
  if (!workspaceLists.data) return null;

  return (
    <>
      {workspaceLists.data.map(({ id, name, quizzesCount }) => (
        <SidebarItem
          key={id}
          onClick={() => setDrawerOpen(false)}
          icon={<IconShared />}
          isSelected={workspaceId === id}
          itemCount={quizzesCount}
          href={`#workspaces/${id}/library`}
        >
          {name}
        </SidebarItem>
      ))}
    </>
  );
}

function PersonalItem({
  currentWorkspaceId,
  workspaceItemsCount,
  applicationContext,
}: {
  currentWorkspaceId?: string;
  workspaceItemsCount: number;
  applicationContext: TeacherApplicationContext;
}): JSX.Element {
  const { setDrawerOpen } = useDrawer();
  const isSelected =
    currentWorkspaceId === null || currentWorkspaceId === undefined;

  return (
    <SidebarItem
      onClick={() => setDrawerOpen(false)}
      icon={<IconUser />}
      href={
        applicationContext === TeacherApplicationContext.Socrative
          ? '/teacher/#quizzes'
          : '#quizzes/'
      }
      isSelected={isSelected}
      itemCount={workspaceItemsCount}
    >
      {translate('Personal')}
    </SidebarItem>
  );
}

function JoinOrCreateSharedLibraryItem({
  openCreateWorkspaceDialog,
  openJoinWorkspaceWithCodeDialog,
  canCreateLibrary,
  featurePaywallDispatch,
}: {
  openCreateWorkspaceDialog: () => void;
  openJoinWorkspaceWithCodeDialog: () => void;
  canCreateLibrary: boolean;
  featurePaywallDispatch: PaywallDispatch;
}) {
  const { x, y, reference, floating } = useFloating({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
  });

  return (
    <>
      <Menu>
        <MenuButton ref={reference}>
          {({ props }) => (
            <div {...props}>
              <SidebarItem
                icon={
                  <IconPlus
                    aria-label={translate('Join or Create Library')}
                    className="text-grey-900"
                  />
                }
                elementType="button"
                isSelected={false}
              >
                {translate('Join or Create Library')}
              </SidebarItem>
            </div>
          )}
        </MenuButton>
        <Portal>
          {
            <MenuList
              ref={floating}
              style={{
                position: 'absolute',
                top: y ?? '',
                left: x ?? '',
              }}
              className="absolute z-101 mt-2 py-1 bg-white overflow-hidden max-w-xl rounded-2 outline-none shadow-menu"
            >
              {/* TODO: Replace with join workspace dialog */}
              <StyledMenuItem
                onClick={() => {
                  openJoinWorkspaceWithCodeDialog();
                }}
              >
                {translate('Join Library')}
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  if (canCreateLibrary) {
                    openCreateWorkspaceDialog();
                  } else {
                    featurePaywallDispatch({
                      type: 'OPEN',
                      touch: [PaywallScreens.SharedLibraryAddLibrary],
                    });
                  }
                }}
              >
                <div className="flex items-center gap-1">
                  {translate('Create Library')}
                  {!canCreateLibrary && (
                    <Badge className="ml-2" tone={'pro'}>
                      PRO
                    </Badge>
                  )}
                </div>
              </StyledMenuItem>
            </MenuList>
          }
        </Portal>
      </Menu>
    </>
  );
}
